import { Money } from "@hubrise/react-components"

import { IPriceOverride } from "shared/hubrise/models"

export function removeVariant(priceOverrides: Array<IPriceOverride>, variantRef: string): Array<IPriceOverride> {
  return normalisePriceOverrides(
    priceOverrides.map((priceOverride) => {
      return {
        ...priceOverride,
        variantRefs:
          priceOverride.variantRefs === undefined
            ? undefined
            : priceOverride.variantRefs.filter((ref) => ref !== variantRef),
      }
    }),
  )
}

export function variantPrice(priceOverrides: Array<IPriceOverride>, variantRef: string): Money | null {
  const variantPriceOverride = priceOverrides.find((po) => po.variantRefs?.includes(variantRef) === true)
  return variantPriceOverride ? variantPriceOverride.price : null
}

export function setVariantPrice(
  priceOverrides: Array<IPriceOverride>,
  variantRef: string,
  price: Money | null,
): Array<IPriceOverride> {
  if (price === null) {
    return removeVariant(priceOverrides, variantRef)
  } else {
    return normalisePriceOverrides([
      ...priceOverrides,
      {
        variantRefs: [variantRef],
        price,
      },
    ])
  }
}

/**
 * Group price overrides by price and remove duplicate variants. If a variant is present in multiple price overrides,
 * the price of the last one will be used. Sort by increasing price.
 * @param priceOverrides
 */
function normalisePriceOverrides(priceOverrides: Array<IPriceOverride>): Array<IPriceOverride> {
  // 1. Build a map variant ref -> price. Override existing variants with the last price.
  const variantRefToPrice = new Map<string, Money>()
  for (const priceOverride of priceOverrides) {
    for (const variantRef of priceOverride.variantRefs || []) {
      variantRefToPrice.set(variantRef, priceOverride.price)
    }
  }

  // 2. Group variants by price. We need to convert Money to string, because Map uses === to compare keys.
  const priceToVariantRefs = new Map<string, Array<string>>()
  for (const [variantRef, price] of variantRefToPrice.entries()) {
    const key = price.toString()
    const variantRefs = priceToVariantRefs.get(key) || []
    variantRefs.push(variantRef)
    priceToVariantRefs.set(key, variantRefs)
  }

  // 3. Build the new price overrides.
  const otherOverrides = priceOverrides.filter((priceOverride) => priceOverride.variantRefs === undefined)
  const variantRefOverrides = Array.from(priceToVariantRefs.entries())
    .map(([price, variantRefs]) => ({
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      variantRefs,
      price: Money.fromString(price),
    }))
    .sort(({ price: price1 }, { price: price2 }) => price1.cents - price2.cents)
  // Two caveats:
  // - Variant refs take higher precedence than other overrides.
  // - Overrides with multiple conditions including variant refs are not supported.
  return [...otherOverrides, ...variantRefOverrides]
}
